/**
 * ChildCard Component
 * Displays child information in a clickable card format for contract details
 */

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { useChild } from '@/hooks/useChildren';
import { useImageUrl } from '@/hooks/useImage';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';
import { formatBirthDateWithAge } from '@/utils/formatChildAge';
import { Skeleton } from '@/components/ui/skeleton';
import { ChildLink } from '@/components/shared/entity-links';

interface ChildCardProps {
  /** Child ID to fetch and display */
  childId: string;
}

// Type for child data that handles both camelCase and snake_case field names
interface ChildData {
  id: string;
  firstname?: string;
  first_name?: string;
  lastname?: string;
  last_name?: string;
  childInternalId?: string;
  child_internal_id?: string;
  birthdate?: string;
  birth_date?: string;
  avatarUrl?: string;
  [key: string]: unknown;
}

export function ChildCard({ childId }: ChildCardProps) {
  const { i18n } = useLingui();
  const { data: childData, isLoading } = useChild(childId);

  // Type the child data properly
  const child = childData as ChildData | undefined;

  // Handle both camelCase and snake_case field names
  const birthdate = child?.birthdate || child?.birth_date;
  const avatarUrlRaw = child?.avatarUrl;
  const firstname = child?.firstname || child?.first_name;
  const lastname = child?.lastname || child?.last_name;

  // Handle empty strings by treating them as null/undefined
  const avatarUrl = avatarUrlRaw?.trim() || undefined;

  // Check if avatarUrl is an image ID that needs resolution
  const needsImageResolution = isImageId(avatarUrl);

  // Use the useImageUrl hook to resolve image ID to URL if needed
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  // Determine the final avatar URL to use
  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined; // Invalid or empty value
  }

  const getInitials = (firstname?: string, lastname?: string) => {
    const firstInitial = firstname?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastname?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  const formatBirthDate = (birthDate: string | undefined) => {
    return formatBirthDateWithAge(birthDate, i18n.locale, i18n);
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border p-3 bg-card">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="rounded-lg border p-3 bg-card">
        <div className="text-sm text-muted-foreground">
          {i18n._(msg`Child information not available`)}
        </div>
      </div>
    );
  }

  const initials = getInitials(firstname, lastname);
  const formattedBirthdate = formatBirthDate(birthdate);

  return (
    <div className="rounded-lg border p-3 bg-card">
      <div className="flex items-center gap-3">
        {/* Avatar with initials */}
        <Avatar className="h-10 w-10 flex-shrink-0">
          {finalAvatarUrl && (
            <AvatarImage
              src={finalAvatarUrl}
              alt={`${firstname} ${lastname}`}
            />
          )}
          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
            {initials}
          </AvatarFallback>
        </Avatar>

        {/* Child info — canonical clickable child link (icon + name + id) */}
        <div className="flex-1 min-w-0">
          <ChildLink
            childId={childId}
            childInternalId={child.childInternalId || child.child_internal_id}
            firstName={firstname}
            lastName={lastname}
            className="text-sm leading-tight"
          />
          {formattedBirthdate && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {formattedBirthdate}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChildCard;